// Single source of truth for "which models does a run have, and how do we
// label / color / order them." Everything model-related in Deep Signal should
// derive its model set from here rather than hardcoding a fixed list, so the UI
// stays elastic to any model stack (3, 5, gptweb, future models).
//
// Identity comes from the shared MODEL_CATALOG; anything the catalog doesn't
// know about still renders with a humanized fallback rather than being dropped.
import { MODEL_CATALOG } from '@/app/constants/models';

export interface ModelLane {
  id: string;     // raw model id as stored on the run (e.g. "openai", "gptweb")
  label: string;  // friendly display label
  color: string;  // theme color token
  order: number;  // catalog order index, for a stable left-to-right sort
}

const CATALOG_BY_ID: Record<string, { label: string; color: string; order: number }> = {};
MODEL_CATALOG.forEach((m, i) => {
  CATALOG_BY_ID[m.id] = { label: m.label, color: m.color, order: i };
});

function humanize(id: string): string {
  return id.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function resolveModelLane(id: string): ModelLane {
  const c = CATALOG_BY_ID[id];
  return {
    id,
    label: c?.label ?? humanize(id),
    color: c?.color ?? 'var(--muted-foreground)',
    order: c?.order ?? 999,
  };
}

// Ordered set of lanes for whatever models a run actually contains. Pass the
// keys of the run's model_breakdown (or any model-id list).
export function getModelLanes(modelKeys: string[]): ModelLane[] {
  return modelKeys.map(resolveModelLane).sort((a, b) => a.order - b.order);
}
